import { useEffect, useState } from "react";
import { Reveal, Img } from "@/components/landing/LandingSections";
import { MessageCircle, Clock, CalendarCheck, Footprints, Activity, ChevronsDown, Droplets, ShieldAlert, Armchair, Package, BedDouble, Laptop,
  RotateCcw, Flame, CalendarX, ShieldQuestion, Hourglass, Dumbbell, Pill, Repeat, Eye } from "lucide-react";

const PAIN_ICONS = { Footprints, Activity, ChevronsDown, Droplets, ShieldAlert, Armchair, Package, BedDouble, Laptop, RotateCcw, Flame, CalendarX, ShieldQuestion, Hourglass, Dumbbell, Pill, Repeat, Eye };
const Bridge = ({ text, color }) => {
  const i = text.indexOf(". ");
  if (i < 0) return text;
  return <><span className="lp-display font-bold text-slate-900" data-testid="lp-pain-bridge-lead">{text.slice(0, i + 1)}</span>{" "}
    <span className="lp-serif whitespace-nowrap font-normal italic" style={{ color }} data-testid="lp-pain-bridge-accent">{text.slice(i + 2)}</span></>;
};
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const hourLabel = (h) => `${h % 12 || 12}${h < 12 ? "am" : "pm"}`;

export const waLink = (num, msg) => `https://wa.me/${num}?text=${encodeURIComponent(msg || "Hi, I'd like to book a session")}`;

const clinicNow = (tz) => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: tz, weekday: "short", hour: "numeric", minute: "numeric", hourCycle: "h23" })
    .formatToParts(new Date()).map((p) => [p.type, p.value]));
  return { day: DAYS.indexOf(parts.weekday), hour: Number(parts.hour) + Number(parts.minute) / 60 };
};

export const availability = (studio) => {
  if (!studio?.open_hours) return null;
  const { day, hour } = clinicNow(studio.timezone || "Asia/Singapore");
  const today = studio.open_hours[day];
  const open = today && hour >= today[0] && hour < today[1];
  return open ? { open, text: `Open today until ${hourLabel(today[1])} · Same-day and next-day appointments available` }
    : { open, text: "Closed now · Message us and we'll reply when we open" };
};

export const Availability = ({ studio, slots, light, testid }) => {
  const [a, setA] = useState(() => availability(studio));
  useEffect(() => {
    const t = setInterval(() => setA(availability(studio)), 60000);
    return () => clearInterval(t);
  }, [studio]);
  if (!a) return null;
  return (
    <div className={`space-y-1.5 text-sm ${light ? "text-white/85" : "text-slate-600"}`} data-testid={testid}>
      <div className="flex items-start gap-2" data-testid={`${testid}-status`}>
        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${a.open ? "bg-[#25D366] shadow-[0_0_0_4px_rgba(37,211,102,0.2)]" : "bg-slate-400"}`} />
        <span>{a.text}</span>
      </div>
      {slots > 0 && <div className="flex items-center gap-2 font-semibold" data-testid={`${testid}-slots`}><CalendarCheck className="h-4 w-4 shrink-0" />Only {slots} new-patient slots left this week</div>}
    </div>
  );
};

export const FreeChat = ({ href, onClick, light, testid }) => (
  <a href={href} onClick={onClick} target="_blank" rel="noreferrer" data-testid={testid}
    className={`group inline-flex items-center gap-2 text-sm font-semibold underline-offset-4 hover:underline ${light ? "text-white" : "text-slate-900"}`}>
    <MessageCircle className="h-4 w-4 text-[#25D366]" />Not sure? Chat with a physio for free
    <span className="transition-transform duration-200 group-hover:translate-x-0.5">→</span>
  </a>
);

export const SoundFamiliar = ({ p, color }) => p ? (
  <section className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-14 md:grid-cols-[1.1fr_1fr] md:gap-14 md:py-20" data-testid="lp-sound-familiar">
    <Reveal>
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Is this you?</div>
      <h2 className="lp-display mt-4 text-4xl font-bold leading-[1.08] text-slate-900 md:text-5xl">Sound <span className="lp-serif font-normal italic" style={{ color }}>familiar?</span></h2>
      <ul className="mt-8 space-y-3" data-testid="lp-pain-points">
        {p.items.map(([icon, text, sub], i) => {
          const Icon = PAIN_ICONS[icon] || Clock;
          return (
            <li key={text} data-testid={`lp-pain-point-${i}`} style={{ transitionDelay: `${i * 60}ms` }}
              className="flex items-center gap-4 rounded-2xl bg-white px-4 py-3.5 shadow-[0_6px_18px_-14px_rgba(15,23,42,0.4)] ring-1 ring-slate-200/70 transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-md">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#eef5fb]"><Icon className="h-5 w-5" strokeWidth={1.5} style={{ color }} /></span>
              <span className="min-w-0">
                <span className="block text-[17px] font-bold leading-snug text-slate-900" data-testid={`lp-pain-title-${i}`}>{text}</span>
                {sub && <span className="mt-0.5 block text-[15px] leading-snug text-slate-500" data-testid={`lp-pain-sub-${i}`}>{sub}</span>}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="mt-9 text-[25px] leading-tight md:text-[32px]" data-testid="lp-pain-bridge"><Bridge text={p.bridge} color={color} /></p>
      <a href="#book" data-testid="lp-pain-book" className="lp-btn mt-7 inline-block rounded-full px-7 py-3.5 font-semibold text-white" style={{ background: color }}>Book my assessment</a>
    </Reveal>
    {p.image && <Reveal><Img img={p.image} alt="Physiotherapy at The Rehab Lab" className="aspect-[4/5] h-auto object-[50%_55%]" testid="lp-pain-image" /></Reveal>}
  </section>
) : null;
